import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { ModalHeader, ModalBody, ModalFooter } from '@/components/Modal'
import { Modal } from '@/screens/shared/ModalScrim'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { ProcedureStep } from '@/components/WizardParts'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { M_OPTGAP_COPY } from '@/copy/modals'
import type { Problem } from '@/data/apiClient'

/**
 * `m-optgap` — process/09-SCREEN-SPECS/m-optgap.md. The acknowledgement that closes §12.4.5's
 * optional-scope gap; W144's fix for the deviation W142 introduced (`ob-wz-4` used to fire
 * `scopeCheckAcknowledgeGaps` directly off its own Continue button — this modal is now the actual
 * confirming surface between that button and the call, per the spec's `M1`–`M9`).
 *
 * `gaps` is the caller's own filter of `ScopeCheckRun.rows` down to the ones in `optional_gaps` —
 * each row's `label` is server-formatted "in the customer's words" (`ScopeCheckRun.rows[].label`
 * description), which is what satisfies rule 2 (name the artefact, not the scope string) and rule
 * 3 (P3-gated wording) at once: nothing here composes its own description of what a scope means.
 *
 * **`note` added W159** — both procedure-step notes were already rendered, as raw `<p>`s instead
 * of the shared `Note` component.
 */
export function MOptgap({
  orgId,
  workspaceId,
  runId,
  workspaceName,
  gaps,
  onDismiss,
  onAcknowledged,
}: {
  orgId: string
  workspaceId: string
  runId: string
  workspaceName: string
  gaps: Array<{ scope: string; label: string }>
  onDismiss: () => void
  onAcknowledged: () => void
}) {
  const acknowledge = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/scope-checks/{run_id}/acknowledge', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, run_id: runId } },
        body: { acknowledged_scopes: gaps.map((g) => g.scope) },
      })
      return unwrap(response as { data?: unknown; error?: Problem })
    },
    onSuccess: onAcknowledged,
  })

  const problem = acknowledge.error instanceof ApiProblemError ? acknowledge.error.problem : undefined
  const headingId = 'm-optgap-heading'

  return (
    <Modal dataScr={unitId('m-optgap')} labelledBy={headingId} onDismiss={onDismiss} maxWidth={520}>
      <ModalHeader id={headingId} heading={M_OPTGAP_COPY.heading}>
        {M_OPTGAP_COPY.subheadingTemplate(gaps.map((g) => g.scope).join(', '))}
      </ModalHeader>
      <ModalBody>
        {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <p className="text-fail text-body">{problem.title}</p>)}
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-7 overflow-hidden">
          {gaps.map((gap, i) => (
            <ProcedureStep key={gap.scope} ordinal={String(i + 1)} boxed>
              <b>{M_OPTGAP_COPY.lostTitle}</b>
              <Note>{M_OPTGAP_COPY.lostNoteTemplate(gap.label, workspaceName)}</Note>
            </ProcedureStep>
          ))}
          <ProcedureStep ordinal="✓" boxed>
            <b>{M_OPTGAP_COPY.laterTitle}</b>
            <Note>{M_OPTGAP_COPY.laterNote}</Note>
          </ProcedureStep>
        </div>
        <Strip>{M_OPTGAP_COPY.auditNote}</Strip>
      </ModalBody>
      <ModalFooter>
        <Button data-act={undefined} ghost onClick={onDismiss}>
          {M_OPTGAP_COPY.backButton}
        </Button>
        <Button data-act={actionId('scopeCheck.acknowledgeGaps')} disabled={acknowledge.isPending} onClick={() => acknowledge.mutate()}>
          {M_OPTGAP_COPY.continueButton}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
